import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Eye, Palette, Type, MousePointer, Minus, RotateCcw, X,
  ArrowUp, Accessibility, AlignJustify, Underline, ZoomIn, Sun,
  EyeOff, Focus
} from "lucide-react";

const A11Y_KEY = "a11y_settings";

const defaults = {
  fontSize: 100,
  highContrast: false,
  grayscale: false,
  highlightLinks: false,
  underlineLinks: false,
  readableFont: false,
  dyslexiaFont: false,
  bigCursor: false,
  stopAnimations: false,
  lineHeight: false,
  textSpacing: false,
  focusHighlight: false,
  hideImages: false,
  bigText: false,
  darkMode: false,
};

const T = {
  title:          { en: "Accessibility",     ru: "Доступность",          he: "נגישות והתאמות" },
  fontSize:       { en: "Font Size",         ru: "Размер шрифта",         he: "גודל גופן" },
  highContrast:   { en: "High Contrast",     ru: "Высокий контраст",      he: "ניגודיות גבוהה" },
  grayscale:      { en: "Grayscale",         ru: "Оттенки серого",         he: "גווני אפור" },
  highlightLinks: { en: "Highlight Links",   ru: "Подсветка ссылок",      he: "הדגש קישורים" },
  underlineLinks: { en: "Underline Links",   ru: "Подчеркнуть ссылки",    he: "קו תחת קישורים" },
  readableFont:   { en: "Readable Font",     ru: "Читаемый шрифт",        he: "גופן קריא" },
  dyslexiaFont:   { en: "Dyslexia Font",     ru: "Шрифт дислексии",       he: "גופן דיסלקציה" },
  bigCursor:      { en: "Large Cursor",      ru: "Большой курсор",        he: "סמן גדול" },
  stopAnimations: { en: "Stop Animations",  ru: "Без анимаций",           he: "עצור אנימציות" },
  lineHeight:     { en: "Line Spacing",      ru: "Высота строки",         he: "מרווח שורות" },
  textSpacing:    { en: "Letter Spacing",    ru: "Межбуквенный",          he: "ריווח אותיות" },
  focusHighlight: { en: "Focus Indicator",  ru: "Выделение фокуса",       he: "הדגש מיקוד" },
  hideImages:     { en: "Hide Images",       ru: "Скрыть картинки",       he: "הסתר תמונות" },
  bigText:        { en: "Bold Text",         ru: "Жирный текст",          he: "טקסט מודגש" },
  darkMode:       { en: "Dark Mode",         ru: "Тёмная тема",           he: "מצב כהה" },
  reset:          { en: "Reset all",         ru: "Сбросить всё",          he: "איפוס הכל" },
  compliance:     {
    en: "Complies with Israeli accessibility regulations (2013)",
    ru: "Соответствует израильским требованиям доступности (2013)",
    he: 'עומד בדרישות תקנות שוויון זכויות לאנשים עם מוגבלות (התשע"ג-2013)',
  },
};

function lbl(key, lang) {
  return T[key]?.[lang] || T[key]?.en || key;
}

// All widget dimensions in px — immune to html font-size changes
const BTN_SIZE = 48;
const BTN_BOTTOM = 20;
const BTN_LEFT = 16;
const PANEL_W = 340;
const PANEL_BOTTOM = BTN_BOTTOM + BTN_SIZE + 12;

export function AccessibilityWidget() {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const panelRef = useRef(null);

  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(A11Y_KEY);
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch {}
    return defaults;
  });

  // Listen for theme changes from Navbar
  useEffect(() => {
    const handler = () => {
      try {
        const saved = localStorage.getItem(A11Y_KEY);
        if (saved) setSettings(prev => ({ ...prev, ...JSON.parse(saved) }));
      } catch {}
    };
    window.addEventListener("theme-change", handler);
    return () => window.removeEventListener("theme-change", handler);
  }, []);

  // Open from navbar mobile button
  useEffect(() => {
    const handler = () => setOpen(o => !o);
    window.addEventListener("open-a11y", handler);
    return () => window.removeEventListener("open-a11y", handler);
  }, []);

  // Support widget open state
  useEffect(() => {
    const handler = (e) => {
      const isOpen = Boolean(e?.detail?.open);
      setSupportOpen(isOpen);
      if (isOpen) setOpen(false);
    };
    window.addEventListener("support-open-state", handler);
    return () => window.removeEventListener("support-open-state", handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        const btn = document.querySelector("[data-testid='a11y-widget-btn']");
        if (btn && btn.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Apply settings to DOM
  useEffect(() => {
    localStorage.setItem(A11Y_KEY, JSON.stringify(settings));
    const html = document.documentElement;
    const body = document.body;

    // Font size on html so rem values scale
    html.style.fontSize = `${settings.fontSize}%`;

    // Filter classes MUST go on <html> — applying filter to body breaks position:fixed
    const htmlClassMap = {
      "a11y-high-contrast": settings.highContrast,
      "a11y-grayscale":     settings.grayscale,
    };
    Object.entries(htmlClassMap).forEach(([cls, on]) => html.classList.toggle(cls, on));

    // All other classes go on body (no filter = no fixed-context issue)
    const bodyClassMap = {
      "a11y-highlight-links": settings.highlightLinks,
      "a11y-underline-links": settings.underlineLinks,
      "a11y-readable-font":   settings.readableFont,
      "a11y-dyslexia-font":   settings.dyslexiaFont,
      "a11y-big-cursor":      settings.bigCursor,
      "a11y-stop-anim":       settings.stopAnimations,
      "a11y-line-height":     settings.lineHeight,
      "a11y-text-spacing":    settings.textSpacing,
      "a11y-focus-highlight": settings.focusHighlight,
      "a11y-hide-images":     settings.hideImages,
      "a11y-big-text":        settings.bigText,
    };
    Object.entries(bodyClassMap).forEach(([cls, on]) => body.classList.toggle(cls, on));
  }, [settings]);

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));
  const reset = () => setSettings(defaults);

  const OPTIONS = [
    { key: "highContrast",   icon: Eye },
    { key: "grayscale",      icon: Palette },
    { key: "highlightLinks", icon: Focus },
    { key: "underlineLinks", icon: Underline },
    { key: "readableFont",   icon: Type },
    { key: "dyslexiaFont",   icon: AlignJustify },
    { key: "bigCursor",      icon: MousePointer },
    { key: "bigText",        icon: ZoomIn },
    { key: "stopAnimations", icon: Minus },
    { key: "lineHeight",     icon: ArrowUp },
    { key: "textSpacing",    icon: Type },
    { key: "focusHighlight", icon: Sun },
    { key: "hideImages",     icon: EyeOff },
  ];

  const activeCount = Object.values(settings).filter(v => v === true).length;

  return (
    <>
      {/* ── Floating button — all px to prevent jump on font-size change ── */}
      <button
        data-testid="a11y-widget-btn"
        data-a11y-widget="1"
        aria-label={lbl("title", lang)}
        title={lbl("title", lang)}
        onClick={() => setOpen(o => !o)}
        style={{
          position: "fixed",
          zIndex: 110,
          bottom: `calc(env(safe-area-inset-bottom, 0px) + ${BTN_BOTTOM}px)`,
          left: `${BTN_LEFT}px`,
          width: `${BTN_SIZE}px`,
          height: `${BTN_SIZE}px`,
          borderRadius: "50%",
          background: "#1565C0",
          boxShadow: "0 4px 20px rgba(21,101,192,0.35)",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "opacity 0.2s, transform 0.15s",
          opacity: supportOpen ? 0 : 1,
          pointerEvents: supportOpen ? "none" : "auto",
          transform: open ? "scale(0.92)" : "scale(1)",
          flexShrink: 0,
        }}
      >
        <Accessibility style={{ width: 22, height: 22, color: "#fff", flexShrink: 0 }} strokeWidth={2.2} />
        {activeCount > 0 && (
          <span style={{
            position: "absolute",
            top: -3,
            right: -3,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#E1306C",
            color: "#fff",
            fontSize: 9,
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid var(--bg-primary)",
            flexShrink: 0,
          }}>
            {activeCount > 9 ? "9+" : activeCount}
          </span>
        )}
      </button>

      {/* ── Panel ── */}
      {open && (
        <>
          {/* Mobile backdrop */}
          <div
            style={{
              position: "fixed", inset: 0, zIndex: 109,
              background: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(3px)",
            }}
            onClick={() => setOpen(false)}
          />

          <div
            ref={panelRef}
            data-testid="a11y-panel"
            data-a11y-widget="1"
            style={{
              position: "fixed",
              zIndex: 110,
              bottom: `calc(env(safe-area-inset-bottom, 0px) + ${PANEL_BOTTOM}px)`,
              left: `${BTN_LEFT}px`,
              width: `min(${PANEL_W}px, calc(100vw - ${BTN_LEFT * 2}px))`,
              background: "var(--bg-secondary)",
              border: "1px solid var(--border-color)",
              borderRadius: 20,
              boxShadow: "0 16px 48px rgba(0,0,0,0.28)",
              animation: "a11ySlideUp 0.2s cubic-bezier(0.34,1.4,0.64,1) both",
            }}
          >
            <style>{`
              @keyframes a11ySlideUp {
                from { opacity:0; transform:translateY(10px) scale(0.97); }
                to   { opacity:1; transform:translateY(0) scale(1); }
              }
              .a11y-opt-btn {
                display:flex; align-items:center; gap:8px;
                padding:9px 10px; border-radius:12px;
                border:1px solid transparent;
                cursor:pointer; transition:all 0.15s;
                font-size:11px; font-weight:500;
                width:100%; text-align:start;
                background:transparent;
                color:var(--text-secondary);
              }
              .a11y-opt-btn:hover { background:var(--surface-2); }
              .a11y-opt-btn.active {
                background:rgba(21,101,192,0.1);
                border-color:rgba(21,101,192,0.25);
                color:#1565C0;
              }
            `}</style>

            {/* Header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 16px 10px",
              borderBottom: "1px solid var(--border-color)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: "#1565C0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Accessibility style={{ width: 15, height: 15, color: "#fff" }} strokeWidth={2.2} />
                </div>
                <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>
                  {lbl("title", lang)}
                </span>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {activeCount > 0 && (
                  <button
                    onClick={reset}
                    title={lbl("reset", lang)}
                    style={{ width: 28, height: 28, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)" }}
                    data-testid="a11y-reset"
                  >
                    <RotateCcw style={{ width: 14, height: 14 }} />
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  style={{ width: 28, height: 28, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)" }}
                >
                  <X style={{ width: 14, height: 14 }} />
                </button>
              </div>
            </div>

            <div style={{ padding: "12px 12px 14px" }}>

              {/* Toggle grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {OPTIONS.map(({ key, icon: Icon }) => (
                  <button
                    key={key}
                    className={`a11y-opt-btn${settings[key] ? " active" : ""}`}
                    onClick={() => update(key, !settings[key])}
                    data-testid={`a11y-${key}`}
                  >
                    <Icon style={{ width: 14, height: 14, flexShrink: 0 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {lbl(key, lang)}
                    </span>
                  </button>
                ))}
              </div>

              {/* Compliance */}
              <div style={{
                marginTop: 12, paddingTop: 10,
                borderTop: "1px solid var(--border-color)",
                fontSize: 10, color: "var(--text-secondary)", textAlign: "center", lineHeight: 1.5,
              }}>
                {lbl("compliance", lang)}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
